import React, { useEffect, useRef, useState } from 'react';
import { FilterDimensionType } from '../../utils/reportFilters';
import {
  FilterDimensionOption,
  addable_dimension_rows,
  append_dimension_option,
  option_key,
  remove_dimension_option,
} from './filterDimensionMultiSelect';

export type { FilterDimensionOption };
export {
  append_dimension_option,
  option_key,
  remove_dimension_option,
} from './filterDimensionMultiSelect';

function filter_search_field_padding_right(selected_count: number, loading: boolean): string {
  if (selected_count > 0 && loading) return '5.5rem';
  if (selected_count > 0) return '4.5rem';
  if (loading) return '3.5rem';
  return '2.75rem';
}

function SearchDropdownChevron({ open }: { open: boolean }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      style={{
        transform: open ? 'rotate(180deg)' : 'none',
        transition: 'transform 0.15s ease',
      }}
      aria-hidden
    >
      <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function OptionRow({ item }: { item: FilterDimensionOption }) {
  if (item.code) {
    return (
      <div>
        <div style={{ fontWeight: 500 }}>{item.name}</div>
        <div style={{ fontSize: '0.85rem', color: 'var(--report-text-muted)' }}>{item.code}</div>
      </div>
    );
  }
  return <div style={{ fontWeight: 500 }}>{item.name}</div>;
}

export interface FilterDimensionMultiSelectProps {
  filter_type: Exclude<FilterDimensionType, 'all'>;
  selected: FilterDimensionOption[];
  on_selected_change: (next: FilterDimensionOption[]) => void;
  search: string;
  on_search_change: (value: string) => void;
  results: FilterDimensionOption[];
  loading: boolean;
  exclude_ids?: Set<string>;
  input_id?: string;
}

const FilterDimensionMultiSelect: React.FC<FilterDimensionMultiSelectProps> = ({
  filter_type,
  selected,
  on_selected_change,
  search,
  on_search_change,
  results,
  loading,
  exclude_ids = new Set<string>(),
  input_id,
}) => {
  const [show_drop, set_show_drop] = useState(false);
  const root_ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const close = (e: PointerEvent) => {
      const t = e.target;
      if (!t || !(t instanceof Node)) return;
      if (root_ref.current?.contains(t)) return;
      set_show_drop(false);
    };
    document.addEventListener('pointerdown', close, true);
    return () => document.removeEventListener('pointerdown', close, true);
  }, []);

  const add_rows = addable_dimension_rows(results, selected, exclude_ids);

  const add_item = (item: FilterDimensionOption) => {
    const next = append_dimension_option(selected, item, exclude_ids);
    if (next === selected) return;
    on_selected_change(next);
  };

  const remove_item = (item: FilterDimensionOption) => {
    on_selected_change(remove_dimension_option(selected, item));
  };

  const type_label = filter_type.charAt(0).toUpperCase() + filter_type.slice(1);

  return (
    <div style={{ position: 'relative' }} ref={root_ref}>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      <label
        htmlFor={input_id}
        style={{
          display: 'block',
          marginBottom: '0.5rem',
          fontWeight: '500',
          color: 'var(--report-text)',
        }}
      >
        Search {type_label}
      </label>
      <div style={{ position: 'relative' }}>
        <input
          id={input_id}
          type="text"
          value={search}
          onChange={(e) => {
            on_search_change(e.target.value);
            set_show_drop(true);
          }}
          onFocus={() => set_show_drop(true)}
          placeholder={`Search ${filter_type}…`}
          style={{
            padding: '0.5rem',
            paddingRight: filter_search_field_padding_right(selected.length, loading),
            border: '1px solid var(--report-border)',
            borderRadius: '4px',
            fontSize: '1rem',
            minWidth: '250px',
            backgroundColor: 'var(--report-surface)',
            color: 'var(--report-text)',
          }}
        />
        <div
          style={{
            position: 'absolute',
            right: '0.2rem',
            top: '50%',
            transform: 'translateY(-50%)',
            display: 'flex',
            alignItems: 'center',
            gap: '0.35rem',
            color: 'var(--report-text-muted)',
          }}
        >
          {loading && (
            <svg
              style={{ width: '16px', height: '16px', animation: 'spin 1s linear infinite' }}
              fill="none"
              viewBox="0 0 24 24"
              aria-hidden
            >
              <circle
                style={{ opacity: 0.25 }}
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                style={{ opacity: 0.75 }}
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          )}
          {selected.length > 0 && (
            <span
              title={`${selected.length} selected`}
              style={{
                fontSize: '0.8rem',
                fontWeight: 600,
                color: 'var(--report-text)',
                lineHeight: 1,
                userSelect: 'none',
              }}
            >
              {selected.length}
            </span>
          )}
          <button
            type="button"
            aria-expanded={show_drop}
            aria-label={show_drop ? 'Close filter list' : 'Open filter list'}
            onClick={() => set_show_drop((v) => !v)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0.2rem',
              border: 'none',
              borderRadius: '4px',
              background: 'transparent',
              cursor: 'pointer',
              color: 'var(--report-text-muted)',
            }}
          >
            <SearchDropdownChevron open={show_drop} />
          </button>
        </div>
        {show_drop && (
          <div
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              marginTop: '0.25rem',
              backgroundColor: 'var(--report-surface)',
              border: '1px solid var(--report-border)',
              borderRadius: '4px',
              maxHeight: '260px',
              overflowY: 'auto',
              zIndex: 1000,
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            }}
          >
            {selected.length > 0 && (
              <>
                <div
                  style={{
                    padding: '0.35rem 0.5rem',
                    borderBottom: '1px solid var(--report-border)',
                    background: 'var(--report-surface-subtle)',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: 'var(--report-text)',
                  }}
                >
                  Selected (click to remove)
                </div>
                {selected.map((item) => (
                  <div
                    key={`s-${option_key(item)}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => remove_item(item)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') remove_item(item);
                    }}
                    style={{
                      padding: '0.5rem',
                      cursor: 'pointer',
                      background: 'var(--report-surface)',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--report-row-hover)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--report-surface)';
                    }}
                  >
                    <OptionRow item={item} />
                  </div>
                ))}
              </>
            )}
            {(selected.length > 0 || add_rows.length > 0) && (
              <div
                style={{
                  padding: '0.35rem 0.5rem',
                  borderTop: selected.length > 0 ? '1px solid var(--report-border)' : undefined,
                  borderBottom: add_rows.length > 0 ? '1px solid var(--report-border)' : undefined,
                  background: 'var(--report-surface-subtle)',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  color: 'var(--report-text)',
                }}
              >
                Add more{loading ? ' …' : ''}
              </div>
            )}
            {add_rows.map((item) => (
              <div
                key={`a-${option_key(item)}`}
                role="button"
                tabIndex={0}
                onClick={() => add_item(item)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') add_item(item);
                }}
                style={{ padding: '0.5rem', cursor: 'pointer' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--report-row-hover)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <OptionRow item={item} />
              </div>
            ))}
            {!loading && selected.length === 0 && add_rows.length === 0 && (
              <div
                style={{ padding: '0.75rem', color: 'var(--report-text-muted)', fontSize: '0.9rem' }}
              >
                Type to search for {filter_type}…
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FilterDimensionMultiSelect;
